import { DockIcon } from "../../../icons/dock";
import { PersonPinCircleIcon } from "../../../icons/personPinCircle";
import { ThumbsUpPipelineIcon } from "../../../icons/thumbsUpPipeline";
import { PreviewIcon } from "../../../icons/preview";
import { RateReviewIcon } from "../../../icons/rateReview";
import { formatDate } from "../../../util/helpers";

// Convert an EY email like "john.smith@ey.com" into a display name "John Smith".
// Falls back to the raw value when it cannot be parsed.
function formatEmailAsName(value) {
  if (!value) return "";
  const raw = String(value).trim();
  const localPart = raw.includes("@") ? raw.split("@")[0] : raw;
  if (!localPart) return raw;
  const parts = localPart.split(/[._-]+/).filter(Boolean);
  if (!parts.length) return raw;
  return parts
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(" ");
}

// Prefer API-provided first/last name for the Engagement Manager and fall back
// to deriving a display name from the email when the name fields are absent.
function getEngagementManagerDisplayName(row) {
  if (!row) return "";
  const first = String(row.engagementManagerFirstName || "").trim();
  const last = String(row.engagementManagerLastName || "").trim();
  const full = [first, last].filter(Boolean).join(" ");
  if (full) return full;
  return formatEmailAsName(row.engagementManagerEmail);
}

export const SEARCH_DEBOUNCE_MS = 400;

export const statusClassMap = {
  New: "em-status-badge--new",
  "Awaiting details": "em-status-badge--inprogress",
  "Request Accepted": "em-status-badge--amber",
  "Requested Accepted": "em-status-badge--amber",
  "Request Completed": "em-status-badge--completed",
  "Request Cancelled": "em-status-badge--cancelled",
  "Request In Progress": "em-status-badge--inprogress",
  Cancelled: "em-status-badge--cancelled",
  "Preparer work completed": "em-status-badge--completed",
  "Preparer Work Completed": "em-status-badge--completed",
  InProgress: "em-status-badge--inprogress",
  "Resource Identified": "em-status-badge--inprogress",
  "Pending Acknowledge": "em-status-badge--inprogress",

  "Awaiting preliminary details from lead team": "em-status-badge--amber",
  "Request accepted": "em-status-badge--amber",
  "Canvas access awaited": "em-status-badge--amber",
  "Canvas Access Awaited": "em-status-badge--amber",
  "Resources onboarded": "em-status-badge--amber",
  "Resource Onboarded": "em-status-badge--amber",
  "Work initiated": "em-status-badge--amber",
  "Work Initiated": "em-status-badge--amber",
  "Awaiting additional details from lead team": "em-status-badge--amber",
  "Detailed review completed": "em-status-badge--amber",
  "Detailed Review Completed": "em-status-badge--amber",
  "Second level review completed": "em-status-badge--amber",
  "Second Level Review Completed": "em-status-badge--amber",
};

export const pipelineStageConfig = [
  {
    labelKey: "em_pendingAcknowledge",
    countKey: "pendingAcknowledgement",
    statusButton: "Pending Acknowledgement",
    gradientClass: "pipeline-gradient--orange",
    Icon: ThumbsUpPipelineIcon,
  },
  {
    labelKey: "em_requestAccepted",
    countKey: "requestAccepted",
    statusButton: "Request Accepted",
    gradientClass: "pipeline-gradient--grey",
    Icon: DockIcon,
  },
  {
    labelKey: "em_resourceIdentified",
    countKey: "resourceIdentified",
    statusButton: "Resource Identified",
    gradientClass: "pipeline-gradient--grey",
    Icon: PersonPinCircleIcon,
  },
  {
    labelKey: "em_detailedReviewCompleted",
    countKey: "detailedReviewCompleted",
    statusButton: "Detailed Review Completed",
    gradientClass: "pipeline-gradient--grey",
    Icon: PreviewIcon,
  },
  {
    labelKey: "em_secondLevelReviewCompleted",
    countKey: "secondLevelReviewCompleted",
    statusButton: "Second Level Review Completed",
    gradientClass: "pipeline-gradient--grey",
    Icon: RateReviewIcon,
  },
];

export function getColumnDefs(
  t,
  buildStatusBadge,
  AckIcon,
  onAcknowledge,
  DelegatedIcon,
) {
  return [
    {
      headerCheckboxSelection: true,
      checkboxSelection: true,
      width: 60,
      maxWidth: 60,
      sortable: false,
      suppressMenu: true,
      suppressSizeToFit: true,
    },
    {
      headerName: t("rmq_clientName"),
      field: "clientName",
      minWidth: 95,
      flex: 1.2,
      sortable: true,
      tooltipField: "clientName",
    },
    {
      headerName: t("rmq_engagementName"),
      field: "engagementName",
      minWidth: 95,
      flex: 1.2,
      sortable: true,
      tooltipField: "engagementName",
    },
    {
      headerName: t("rmq_serviceName"),
      field: "serviceName",
      minWidth: 85,
      flex: 1,
      sortable: true,
      tooltipField: "serviceName",
    },
    {
      headerName: t("rmq_engagementCode"),
      field: "engagementCode",
      minWidth: 95,
      flex: 1,
      sortable: true,
      tooltipField: "engagementCode",
      valueFormatter: (params) => params.value || "",
    },
    {
      headerName: t("rmq_status"),
      field: "status",
      minWidth: 115,
      flex: 1.2,
      sortable: true,
      cellRenderer: (params) => buildStatusBadge(params.value),
      cellClass: "em-status-cell",
      tooltipField: "status",
    },
    {
      headerName: t("rmq_creationDate"),
      field: "creationDate",
      minWidth: 95,
      flex: 1,
      sortable: true,
      valueFormatter: (params) => formatDate(params.value),
      tooltipValueGetter: (params) => formatDate(params.value),
    },
    {
      headerName: t("rmq_office"),
      field: "office",
      minWidth: 65,
      flex: 0.7,
      sortable: true,
      tooltipField: "office",
    },
    {
      headerName: t("rmq_country"),
      field: "country",
      minWidth: 65,
      flex: 0.7,
      sortable: true,
      tooltipField: "country",
    },
    {
      headerName: t("rmq_industry"),
      field: "industry",
      minWidth: 85,
      flex: 1,
      sortable: true,
      tooltipField: "industry",
      valueFormatter: (params) => params.value || "",
    },
    {
      headerName: t("em_engagementManager"),
      field: "engagementManagerEmail",
      minWidth: 115,
      flex: 1.3,
      sortable: true,
      valueGetter: (params) => getEngagementManagerDisplayName(params.data),
      tooltipValueGetter: (params) => params.data?.engagementManagerEmail || "",
    },
    {
      headerName: t("em_acknowledged"),
      field: "acknowledged",
      minWidth: 80,
      flex: 0.7,
      sortable: true,
      cellRenderer: (params) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!params.data.acknowledged && onAcknowledge) {
              onAcknowledge(params.data.requestID);
            }
          }}
          className={`em-ack-btn${params.data.acknowledged ? " em-ack-btn--active" : ""}`}
        >
          <AckIcon filled={params.data.acknowledged} />
        </button>
      ),
      cellClass: "em-ack-cell",
    },
    {
      headerName: t("em_delegated"),
      field: "isDelegated",
      minWidth: 80,
      flex: 0.7,
      sortable: true,
      cellRenderer: (params) => (
        <span
          className={`em-delegated-badge${params.data.isDelegated ? " em-delegated-badge--active" : ""}`}
        >
          <DelegatedIcon active={params.data.isDelegated} size={14} />
        </span>
      ),
      cellClass: "em-delegated-cell",
    },
  ];
}
